import { ThunkAction } from 'redux-thunk';

import _flatMap from 'lodash-es/flatMap';

import * as CatalogClient from 'Clients/Catalog';
import RootState from 'Store/Root';
import { YarnSpriteItem } from 'Types/Yarns';
import { Urls as ImageUrls } from 'Clients/Images';
import { ColorFavorite } from 'Types/ColorFavorites';
import { clearColorFavorites } from 'Clients/Catalog/ColorFavorites';
import Config from 'Config';

export const LOADING_COLORFAVORITES = 'LOADING_COLORFAVORITES';
export const LOADED_COLORFAVORITES = 'LOADED_COLORFAVORITES';
export const LOAD_COLORFAVORITES_FAILED = 'LOAD_COLORFAVORITES_FAILED';
export const SET_COLORFAVORITES = 'SET_COLORFAVORITES';
export const SET_SELECTED_COLORFAVORITE = 'SET_SELECTED_COLORFAVORITE';

export interface ColorFavoritesAction {
  type: string;
  items?: ColorFavorite[];
  selectedItemName: string;
}

function loadingColorFavorites(): ColorFavoritesAction {
  return {
    type: LOADING_COLORFAVORITES,
    selectedItemName: ''
  };
}

function colorFavoritesLoadFailed(): ColorFavoritesAction {
  return {
    type: LOAD_COLORFAVORITES_FAILED,
    selectedItemName: ''
  };
}

function colorFavoritesLoaded(items: ColorFavorite[], selectedItemName?: string): ColorFavoritesAction {
  return {
    type: LOADED_COLORFAVORITES,
    items: items,
    selectedItemName: getDefaultSelectedColorFavorite(items, selectedItemName)
  };
}

function getDefaultSelectedColorFavorite(colorFavorites: ColorFavorite[], selectedColorFavorite: string): string {
  if (selectedColorFavorite) {
    if (colorFavorites && colorFavorites.length > 0 && colorFavorites.some(cf => cf.name === selectedColorFavorite)) {
      return selectedColorFavorite;
    }
  }

  if (colorFavorites && colorFavorites.length > 0) {
    return colorFavorites[0].name;
  }

  return '';
}

export function loadColorFavorites(): ThunkAction<Promise<any>, RootState, any> {
  return (dispatch, getState) => {
    const state = getState();

    if (!Config.studio.showColorFavorties) {
      return Promise.resolve(true);
    }

    if (!state.user.status.loggedIn) {
      return Promise.resolve(true);
    }

    if (state.colorFavorites.loaded || state.colorFavorites.loading) {
      return Promise.resolve(true);
    }

    const existingSelectedColorFavorite = state.colorFavorites.selectedItemName;

    dispatch(loadingColorFavorites());

    return Promise.all([
      CatalogClient.ColorFavorites.getColorFavorites(),
    ]).then(
      x => dispatch(colorFavoritesLoaded(mapColorFavoritesToColorFavorite(x[0], state.yarnLibraries.items), existingSelectedColorFavorite)),
      e => dispatch(colorFavoritesLoadFailed())
    );
  };
}

export function refreshColorFavorites(): ThunkAction<Promise<any>, RootState, any> {
  return (dispatch, getState) => {
    const state = getState();

    if (!Config.studio.showColorFavorties) {
      return Promise.resolve(true);
    }

    if (!state.user.status.loggedIn) {
      return Promise.resolve(true);
    }

    const existingSelectedColorFavorite = state.colorFavorites.selectedItemName;

    dispatch(loadingColorFavorites());

    clearColorFavorites();

    return Promise.all([
      CatalogClient.ColorFavorites.getColorFavorites(),
    ]).then(
      x => dispatch(colorFavoritesLoaded(mapColorFavoritesToColorFavorite(x[0], state.yarnLibraries.items), existingSelectedColorFavorite)),
      e => dispatch(colorFavoritesLoadFailed())
    );
  };
}

function mapColorFavoritesToColorFavorite(colorFavorites: TrykApi.Catalog.IColorFavorites[], libs: TrykApi.Yarns.IYarnLibrary[], size = 15): ColorFavorite[] {

  if (!colorFavorites || colorFavorites.length === 0) {
    return [];
  }

  const yarns = _flatMap(libs.map(lib =>
    lib.yarns.map<YarnSpriteItem>(yarn => {
      const thumbnailUrl = 'url(' + ImageUrls.Thumbnail.yarn(yarn.code, '', yarn.masterLibraryCode, size) + ')';

      return {
        id: yarn.yarnId,
        typeId: yarn.yarnTypeId,
        code: yarn.code,
        label: yarn.code,
        childCodes: yarn.constructions.map(constr => constr.child.code),
        isHighLuster: yarn.properties.isHighLustre,
        background: thumbnailUrl,
      };
    })
  ));

  return colorFavorites.map(colorFavorite => {
    return {
      id: colorFavorite.colorFavoritesId,
      name: colorFavorite.name,
      yarns: colorFavorite.yarns
        .sort((a, b) => { return a.slot - b.slot; })
        .map(x => {
          const result = yarns.find(yarn => yarn.id === x.yarnId);

          if (result === undefined) {
            return {
              id: 0,
              typeId: 0,
              code: '',
              label: '',
              childCodes: [],
              isHighLuster: false,
              background: '',
            };
          }

          return result;
        })
    };
  });
}

export function saveColorFavorites(
  colorFavorites: ColorFavorite[]
): ThunkAction<Promise<boolean>, RootState, any> {
  return (dispatch, getState) => {
    if (!Config.studio.showColorFavorties) {
      return Promise.resolve(true);
    }

    return new Promise<boolean>((resolve, reject) => {
      dispatch<ColorFavoritesAction>({
        type: SET_COLORFAVORITES,
        items: colorFavorites,
        selectedItemName: getState().colorFavorites.selectedItemName,
      });

      updateColorFavorites(getState())
        .then(() => {
          resolve(true);
        })
        .catch(() => {
          reject(false);
        });
    });
  };
}

export function addColorFavorite(
  colorFavoriteName: string
): ThunkAction<Promise<boolean>, RootState, any> {
  return (dispatch, getState) => {
    if (!Config.studio.showColorFavorties) {
      return Promise.resolve(true);
    }

    return new Promise<boolean>((resolve, reject) => {
      // Add a new color favorite with empty yarns
      const existingColorFavorites = getState().colorFavorites.items;

      // Create default empty yarn slots (10 slots)
      const createEmptyYarn = () => ({
        id: 0,
        typeId: 0,
        childCodes: [] as string[],
        code: '',
        label: '',
        masterLibraryId: 0,
        masterLibraryCode: '',
        isHighLuster: false,
        background: ''
      });

      // If there are existing palettes, copy their structure; otherwise create 10 empty slots
      const defaultYarns = existingColorFavorites.length > 0
        ? existingColorFavorites[0].yarns.map(() => createEmptyYarn())
        : Array.from({ length: 10 }, () => createEmptyYarn());

      existingColorFavorites.push({
        id: 0,
        name: colorFavoriteName,
        yarns: defaultYarns
      });

      dispatch<ColorFavoritesAction>({
        type: SET_COLORFAVORITES,
        items: existingColorFavorites,
        selectedItemName: colorFavoriteName,
      });

      // Save the updated color favorites
      updateColorFavorites(getState())
        .then(() => {
          // Refresh the updated color favorites from the server to get the new ids
          dispatch(refreshColorFavorites());
          resolve(true);
        })
        .catch(() => {
          reject(false);
        });
    });
  };
}

export function setSelectedColorFavorites(
  colorFavoriteName: string
): ThunkAction<Promise<boolean>, RootState, any> {
  return (dispatch, getState) => {
    if (!Config.studio.showColorFavorties) {
      return Promise.resolve(true);
    }

    return new Promise<boolean>((resolve, reject) => {
      dispatch<ColorFavoritesAction>({
        type: SET_SELECTED_COLORFAVORITE,
        selectedItemName: colorFavoriteName,
      });

      resolve(true);
    });
  };
}

function updateColorFavorites(state: RootState): Promise<boolean> {
  const colorFavorites: TrykApi.Catalog.IColorFavorites[] = state.colorFavorites.items.map(item => {
    return {
      colorFavoritesId: item.id,
      siteId: 0,
      userId: 0,
      name: item.name,
      yarns: item.yarns.map<TrykApi.Catalog.IColorFavoritesYarns>((yarn, idx) => {
        return {
          colorFavoritesYarnsId: 0,
          colorFavoritesId: item.id,
          yarnId: yarn.id,
          slot: idx
        };
      })
    };
  });

  return CatalogClient.ColorFavorites.saveColorFavorites(colorFavorites);
}
